import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useUpdateUserPasswordMutation ,useLazyGetMyUserQuery, useUpdateUserByIdMutation} from 'app/services/user.service'
import useUploadPhoto from 'utils/hooks/useUploadPhoto'
import { FixMeLater } from 'types'
import { passwordValidator } from 'utils/helpers/passwordValidator'
import { ERRORS, NAVIGATION, TABS } from 'locales/locales'
import {
  confirmPasswordValidation,
  requiredValidation,
} from 'utils/helpers/validations'
import { useAppSelector } from 'app/hooks'
import UserInfo from './user-info/UserInfo'
import ChangePassword from './change-password/ChangePassword'
import CustomModal from 'components/modals/CustomModal'
import Loading from 'components/spinner/Loading'
import UserStatistic from '../../components/user-statistic/UserStatistics'
import Spinner from 'components/spinner/Spinner'
import { authActions } from 'app/slices/auth.slice'
import { useDispatch } from 'react-redux'
import CollapsibleTags from 'components/tags/TagsCollapse'
import { ROLES } from '../../locales/locales';
import { ConfigEnums } from 'utils/configEnums'
import DropdownAlert from 'components/dropdown-alert/DropdownAlert'

interface IAccountSettingsProps {
  onClose: () => void
}

const statisticDataInitial = {
  totalChannels: 0,
  totalMessages: 0,
  totalFiles: 0,
  totalVideoCall: 0,
}
  const AccountSettings = ({ onClose }: IAccountSettingsProps) => {
  const passwordSettingsSlice = useAppSelector((state) => state.system.passwordSettings)
  const myId = useAppSelector((state) => state.auth.id)

  const { t } = useTranslation()
  const [formChecked, setFormChecked] = useState(false)
  const [visibleChangePassword, setVisibleChangePassword] = useState(false)
  const [userStatisticData, setUserStatisticData] = useState(statisticDataInitial)
  const [userInfo, setUserInfo] = useState({
    _id: '',
    avatar: '',
    fullname: '',
    email: '',
    title: '',
    team: '',
    phoneNumber: '',
    isLdapUser: false,
    notificationLanguage: "",
    roleNames: [],
  })
  const [pendingAvatarFile, setPendingAvatarFile] = useState<File | null>(null)
  const [passwordInfo, setPasswordInfo] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  })

  const [updateUser] = useUpdateUserByIdMutation()
  const [updateUserPassword] = useUpdateUserPasswordMutation()
  const [getMyUser, { isFetching }] = useLazyGetMyUserQuery()
  const dispatch = useDispatch();
  const { uploadToServer, isUploadLoading } = useUploadPhoto(() => {
    // Empty callback - we handle upload in onUpdateUserInfo
  }, 'avatar')

  useEffect(() => {
    fetchUserInfo()
  }, [])

  const fetchUserInfo = async () => {
    try {
      const result: FixMeLater = await getMyUser({});
      setUserStatisticData(result?.data?.data?.statistic)
      setUserInfo({ ...userInfo, ...result?.data?.data?.info })
    } catch (err) {
      console.log(err)
    }
  }
  
  const roleTags = userInfo.roleNames.map(roleName => ({ title: roleName }));

  const onUpdateUserInfo = async () => {
    setFormChecked(true)
    const { isValid: isValidName } = requiredValidation(userInfo.fullname)
    if (isValidName) {
      try {
        Loading.ref.show()
        // Eğer yeni bir avatar dosyası seçildiyse, önce onu yükle
  
        if (pendingAvatarFile) {
          try {
            const isUploaded = await uploadToServer(pendingAvatarFile)
           
          } catch (uploadError) {
            Loading.ref.hide()
            DropdownAlert.ref.show({
              isError: true,
              message: t(ERRORS.UPLOAD_FILE_ERROR, { file_name: pendingAvatarFile?.name }),
            })
            return
          }
        }

        const updatedUser = await updateUser({
          _id: myId,
          params: {
            fullname: userInfo?.fullname?.trim(),
            phoneNumber: userInfo?.phoneNumber?.trim(),
            notificationLanguage: userInfo?.notificationLanguage?.trim(),
          },
        })
        if ('data' in updatedUser && updatedUser?.data?.success) {
          dispatch(
            authActions.updateUserInfo({
              fullname: userInfo?.fullname?.trim(),
              phoneNumber: userInfo?.phoneNumber?.trim(),
              notificationLanguage: userInfo?.notificationLanguage?.trim(),
            }),
          )
          setPendingAvatarFile(null) // Başarılı yükleme sonrası temizle
        }
      } catch (error) {
        finishUpdate()
        DropdownAlert.ref.show({
          isError: true,
          message: error as string,
        })
      } finally {
        Loading.ref.hide()
        finishUpdate()
      }
    }
  }

  const onUpdatePassword = async () => {
    setFormChecked(true)
    const { isValid: isRequiredCurrent } = requiredValidation(passwordInfo.currentPassword)
    const { isValid: isRequiredNew } = requiredValidation(passwordInfo.newPassword)
    const { isValid: isRequiredConfirmNew } = requiredValidation(passwordInfo.confirmNewPassword)
    const { isValid: isConfirm } = confirmPasswordValidation(
      passwordInfo.newPassword,
      passwordInfo.confirmNewPassword,
    )

    if (isRequiredCurrent && isRequiredNew && isRequiredConfirmNew && isConfirm) {
      if (passwordSettingsSlice && Object.keys(passwordSettingsSlice)?.length) {
        const validate = passwordValidator(passwordInfo.newPassword, passwordSettingsSlice)
        if (!validate.valid) {
          return
        }
      }
      try {
        Loading.ref.show()
        const data: FixMeLater = await updateUserPassword({
          currentPassword: passwordInfo.currentPassword,
          password: passwordInfo.newPassword,
        })
        //To not close the Modal
        if (data?.error) return
        finishUpdate()
      } finally {
        Loading.ref.hide()
      }
    }
  }

  const uploadAvatar = async (fileUrl: string, fileData?: File) => {
    setUserInfo({ ...userInfo, avatar: fileUrl })
    if (fileData) {
      setPendingAvatarFile(fileData)
    }
  }

  const onChangeUserInfo = (name: string, value: string) => {
    setUserInfo({ ...userInfo, [name]: value })
  }

  const onChangePasswordInfo = (name: string, value: string) => {
    setPasswordInfo({ ...passwordInfo, [name]: value })
  }

  const onClickChangePassword = () => {
    setVisibleChangePassword(true)
  }

  const finishUpdate = () => {
    onClose()
    setFormChecked(false)
  }

  const tab = [{ title: t(TABS.GENERAL), action: true }, { title: t(TABS.STATISTIC) }]

  return (
    <CustomModal
      title={t(NAVIGATION.ACCOUNT_SETTINGS)}
      onCancel={onClose}
      closeModal={onClose}
      onOk={!visibleChangePassword ? onUpdateUserInfo : onUpdatePassword}
      tab={tab}
      showButtons={true}
      loading={isUploadLoading}
    >
      {!visibleChangePassword ? (
        <>
          <UserInfo
            {...userInfo}
            formChecked={formChecked}
            onChange={onChangeUserInfo}
            onUploadPhoto={uploadAvatar}
            onClickChangePassword={onClickChangePassword}
          />
          <CollapsibleTags
            data={roleTags}
            title={t(ROLES.USER_ROLES)}
            maxItemsToShow={ConfigEnums.MaxItemsToShow}
          />
        </>
      ) : (
        <ChangePassword
          {...passwordInfo}
          formChecked={formChecked}
          onChange={onChangePasswordInfo}
        />
      )}
      {isFetching && <Spinner loading />}
      <UserStatistic {...userStatisticData} />
    </CustomModal>
  )
}

export default AccountSettings
